/**
 * Cohesive onboarding widget — invisible state shell.
 *
 * The widget node is registered as a regular React Flow / Loro node so
 * that step state (`step`, `started`, `snapped`, `pdfFileIds`,
 * `imageNodeIds`, `homepageArtifactId`) persists across reload, syncs
 * across collaborators, and survives the sequence end via node
 * removal. But the *visible* onboarding card lives outside the canvas
 * plane: it's a screen-fixed overlay (`OnboardingOverlay`) that reads
 * `activeOnboarding` from `CanvasOnboardingContext`.
 *
 * Splitting state from rendering lets the card dock to viewport
 * corners per step, freeing the canvas as a real drop / interaction
 * surface for drop-heavy steps (explorer drop, multi-select, agent
 * placement, right-click Quick Chat). The widget node itself renders
 * an invisible 1×1 placeholder so React Flow / Loro layout stays
 * happy.
 *
 * Responsibilities:
 *  • Route to the current step based on Loro's `data.step` (1-indexed)
 *    and publish that snapshot to `CanvasOnboardingContext`.
 *  • Drive the cross-step exit/entry animation via the `outgoing`
 *    flag — the overlay reads it and replays exit keyframes for
 *    STEP_EXIT_MS before we mutate Loro / remove the node.
 *  • Forward `onAdvance` / `onFinish` callbacks. Falls through to
 *    `onFinish` when `onAdvance` fires from the last step.
 *  • Provide the dev-only restart by bumping a counter that the
 *    overlay keys off.
 */

import { useUpdateGlobalOnboardingStatus } from "@/api/onboarding";
import { ViewportPortal } from "@xyflow/react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCanvasApi } from "../../../context/CanvasContext";
import { useCanvasOnboardingUpdate } from "../../../context/CanvasOnboardingContext";
import { useOnboardingAgentSession } from "./hooks/useOnboardingAgentSession";
import {
  BRIEF_DROP_TARGET_HEIGHT as BRIEF_TARGET_HEIGHT,
  BRIEF_DROP_TARGET_WIDTH as BRIEF_TARGET_WIDTH,
  STEP_EXIT_MS,
} from "./shared/constants";
import type { OnboardingData } from "./shared/types";
import { ONBOARDING_STEPS } from "./steps";

interface OnboardingWidgetProps {
  id: string;
  data: OnboardingData;
  dragging?: boolean;
  positionAbsoluteX?: number;
  positionAbsoluteY?: number;
}

export const OnboardingWidget = memo(function OnboardingWidget({
  id,
  data,
  positionAbsoluteX = 0,
  positionAbsoluteY = 0,
}: OnboardingWidgetProps) {
  const canvas = useCanvasApi();
  const updateOnboarding = useCanvasOnboardingUpdate();

  // `useUpdateGlobalOnboardingStatus` hands back a fresh `setComplete`
  // identity every render. Snapshot it into a ref so the callbacks
  // below (and everything memoized off them — `snapshot`, the overlay
  // publish effect) stay referentially stable.
  const { setComplete } = useUpdateGlobalOnboardingStatus();
  const setCompleteRef = useRef(setComplete);
  setCompleteRef.current = setComplete;

  // Snapshot the session-teardown callback into a ref for the same
  // reason — keeps `handleFinish` (and the `snapshot` memo / overlay
  // publish effect downstream of it) referentially stable.
  const { openOnboardingAgentSession, deleteOnboardingAgentSession } =
    useOnboardingAgentSession(id, data);
  const openOnboardingAgentSessionRef = useRef(openOnboardingAgentSession);
  openOnboardingAgentSessionRef.current = openOnboardingAgentSession;
  const deleteOnboardingAgentSessionRef = useRef(deleteOnboardingAgentSession);
  deleteOnboardingAgentSessionRef.current = deleteOnboardingAgentSession;

  // 1-based step index. Clamped so a stale persisted step beyond the
  // current registry length falls back to the last available step.
  const totalSteps = ONBOARDING_STEPS.length;
  const rawStep = typeof data.step === "number" ? data.step : 1;
  const step = Math.min(Math.max(1, rawStep), totalSteps);
  const stepDef = ONBOARDING_STEPS[step - 1];
  const isLastStep = step === totalSteps;

  /**
   * Step-transition flag. Set true when a step calls `onAdvance` /
   * `onFinish`; the overlay applies the exit keyframes; after
   * STEP_EXIT_MS we mutate Loro (or remove the node) and the new
   * step's settle entrance plays automatically because the overlay
   * keys its step container on `step:restartCount`.
   */
  const [outgoing, setOutgoing] = useState(false);
  /**
   * Incremented on every dev restart so the overlay's step container
   * `key` changes regardless of whether `step` actually changes —
   * forcing a fresh mount of the active step component, which
   * re-initialises its local state from the (just-reset) Loro data.
   */
  const [restartCount, setRestartCount] = useState(0);

  /**
   * End the onboarding sequence. Marks global onboarding complete so
   * the workspace "Welcome" dialog never reappears, tears down the
   * dedicated onboarding chat session so the user isn't left talking to
   * the onboarding agent, plays the exit animation, then removes the
   * state-shell node.
   */
  const handleFinish = useCallback(() => {
    if (outgoing) return;
    setCompleteRef.current();
    void deleteOnboardingAgentSessionRef.current();
    setOutgoing(true);
    window.setTimeout(() => canvas.removeNode(id), STEP_EXIT_MS);
  }, [outgoing, id, canvas]);

  /**
   * Move to the next step in the registry. From the last step, falls
   * through to `handleFinish` so step components don't have to special-
   * case being last.
   */
  const handleAdvance = useCallback(() => {
    if (outgoing) return;
    if (isLastStep) {
      handleFinish();
      return;
    }
    setOutgoing(true);
    window.setTimeout(() => {
      canvas.updateNodeData(id, { step: step + 1, started: true });
      setOutgoing(false);
      // Surfaced for analytics / external listeners on first advance.
      if (step === 1) {
        window.dispatchEvent(
          new CustomEvent("cohesive:onboarding:start", {
            detail: { nodeId: id },
          }),
        );
      }
    }, STEP_EXIT_MS);
  }, [outgoing, isLastStep, step, id, canvas, handleFinish]);

  /**
   * Dev-only: reset everything to the freshly-placed state. Skips the
   * exit animation — just snaps back. Wipes step-specific persisted
   * fields too so step 1 is fully fresh.
   */
  const handleRestart = useCallback(() => {
    setOutgoing(false);
    setRestartCount((c) => c + 1);
    canvas.updateNodeData(id, {
      step: 1,
      started: false,
      snapped: false,
    });
  }, [id, canvas]);

  /**
   * Publish the active step + callbacks to the onboarding context so
   * `OnboardingOverlay` can render the screen-docked card. Cleared on
   * unmount (node removed at finish, or the canvas itself unmounts).
   */
  const snapshot = useMemo(
    () => ({
      nodeId: id,
      step,
      stepDef,
      data,
      outgoing,
      restartCount,
      advance: handleAdvance,
      finish: handleFinish,
      restart: handleRestart,
      openAgentChat: () => {
        void openOnboardingAgentSessionRef.current();
      },
    }),
    [
      id,
      step,
      stepDef,
      data,
      outgoing,
      restartCount,
      handleAdvance,
      handleFinish,
      handleRestart,
    ],
  );

  useEffect(() => {
    updateOnboarding({ activeOnboarding: snapshot });
    return () => {
      updateOnboarding({ activeOnboarding: null });
    };
  }, [snapshot, updateOnboarding]);

  // During the drag-brief step (and only until the PDF lands), surface a
  // dashed canvas-space target so the user has an obvious place to aim.
  // Sized slightly larger than the file_record node that will appear on
  // drop (default width 350). Pointer-events disabled so it never
  // intercepts the dnd-kit drop onto the underlying canvas droppable.
  const showBriefDropTarget =
    stepDef?.key === "drag-brief" && !data.pdfNodeId && !outgoing;

  return (
    <>
      <div
        aria-hidden
        style={{
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: "none",
          visibility: "hidden",
        }}
      />
      {showBriefDropTarget && (
        <BriefDropTarget
          originX={positionAbsoluteX}
          originY={positionAbsoluteY}
        />
      )}
    </>
  );
});

/**
 * Dashed canvas-space drop target shown during the drag-brief step.
 * Rendered through ReactFlow's `ViewportPortal` so it lives in viewport
 * (canvas) coordinates, not inside the invisible 1×1 widget DOM (whose
 * `opacity:0` / `fit-content` parent chain would otherwise clip or hide
 * it). Sized a hair larger than the default file_record node and
 * centered on the widget's canvas origin so the PDF drops neatly
 * inside. `pointer-events:none` so the canvas droppable beneath still
 * receives the drop event.
 */
// Dimensions come from shared/constants so DragBriefStep can reject
// drops that landed outside this same outline. A touch larger than
// the default file_record / PDF node (350 wide — see imageDimensions
// .ts) so the dropped node lands cleanly inside the target.
const BRIEF_TARGET_STROKE = 6;
const BRIEF_TARGET_RADIUS = 28;
// dasharray = [dash length, gap length] — generous gap reads as
// deliberate "drop here" affordance rather than a tight ghost outline.
const BRIEF_TARGET_DASH = "22 18";

const BriefDropTarget = memo(function BriefDropTarget({
  originX,
  originY,
}: {
  originX: number;
  originY: number;
}) {
  // Inset by half the stroke so the dashed line sits fully inside the
  // box dimensions instead of being clipped at the SVG edge.
  const inset = BRIEF_TARGET_STROKE / 2;

  return (
    <ViewportPortal>
      <svg
        aria-hidden
        width={BRIEF_TARGET_WIDTH}
        height={BRIEF_TARGET_HEIGHT}
        style={{
          position: "absolute",
          left: originX - BRIEF_TARGET_WIDTH / 2,
          top: originY - BRIEF_TARGET_HEIGHT / 2,
          // Near-opaque so the rust outline reads vividly against the
          // canvas in both light and dark themes.
          color: "var(--co-fg, currentColor)",
          opacity: 0.95,
          pointerEvents: "none",
          overflow: "visible",
          zIndex: 0,
        }}
      >
        {/* Border continuously pulses (`_co_onb_drop_pulse`) in rust so
            the outline always reads as a live "drop here" cue. */}
        <rect
          className="_co_onb_drop_pulse"
          x={inset}
          y={inset}
          width={BRIEF_TARGET_WIDTH - BRIEF_TARGET_STROKE}
          height={BRIEF_TARGET_HEIGHT - BRIEF_TARGET_STROKE}
          rx={BRIEF_TARGET_RADIUS}
          ry={BRIEF_TARGET_RADIUS}
          fill="none"
          stroke="#B86E49"
          strokeWidth={BRIEF_TARGET_STROKE}
          strokeDasharray={BRIEF_TARGET_DASH}
          strokeLinecap="round"
        />
        <text
          x={BRIEF_TARGET_WIDTH / 2}
          y={BRIEF_TARGET_HEIGHT / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="currentColor"
          style={{
            fontFamily: "inherit",
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: "-0.01em",
            // Slightly opaque so it doesn't fight the outline at 0.55 svg alpha.
            opacity: 0.85,
          }}
        >
          Drop file here
        </text>
      </svg>
    </ViewportPortal>
  );
});
